// HTTP-Client für die Verwaltungs-API (Pflichtenheft 4.1): Cookie-Sitzung, CSRF-Header für ändernde Anfragen,
// Fehler als problem+json, If-Match aus rowVersion.

export interface Problem {
  type?: string;
  title: string;
  status: number;
  code: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(public readonly problem: Problem) {
    super(problem.title);
  }
  get status() {
    return this.problem.status;
  }
  get code() {
    return this.problem.code;
  }
}

let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null) => {
  csrfToken = t;
};

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null) => {
  onUnauthorized = fn;
};

export const BASE = '/api/v1';
export const etag = (rowVersion: number) => `"${rowVersion}"`;

export interface RequestOptions {
  body?: unknown;
  ifMatch?: number | string;
  headers?: Record<string, string>;
  raw?: BodyInit;
  signal?: AbortSignal;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && method !== 'HEAD' && csrfToken) headers['X-CSRF-Token'] = csrfToken;
  if (opts.ifMatch !== undefined) headers['If-Match'] = typeof opts.ifMatch === 'number' ? etag(opts.ifMatch) : opts.ifMatch;
  const res = await fetch(path.startsWith('/') && !path.startsWith(BASE) ? BASE + path : path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.raw ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const p = (data && typeof data === 'object' && 'code' in data ? data : { title: res.statusText || 'Fehler', status: res.status, code: 'http_error' }) as Problem;
    if (res.status === 401 && !path.includes('/auth/login')) onUnauthorized?.();
    throw new ApiError({ ...p, status: res.status });
  }
  return data as T;
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return undefined;
  }
}

export const api = {
  get: <T>(p: string, o?: RequestOptions) => request<T>('GET', p, o),
  post: <T>(p: string, body?: unknown, o?: RequestOptions) => request<T>('POST', p, { ...o, body: body ?? {} }),
  put: <T>(p: string, body: unknown, o?: RequestOptions) => request<T>('PUT', p, { ...o, body }),
  patch: <T>(p: string, body: unknown, o?: RequestOptions) => request<T>('PATCH', p, { ...o, body }),
  del: <T = void>(p: string, o?: RequestOptions) => request<T>('DELETE', p, o),
};

/** ETag einer Ressource ohne rowVersion im Rumpf (z. B. Mandant), für If-Match. */
export async function fetchEtag(path: string): Promise<string> {
  const res = await fetch(BASE + path, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new ApiError({ title: res.statusText || 'Fehler', status: res.status, code: 'http_error' });
  return res.headers.get('ETag') ?? '"0"';
}

/** Alle Seiten einer Cursor-Liste laden (4.1). */
export async function getAll<T>(path: string, limit = 200): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < 50; i++) {
    const sep = path.includes('?') ? '&' : '?';
    const res: { items: T[]; nextCursor: string | null } = await api.get(`${path}${sep}limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
    out.push(...res.items);
    cursor = res.nextCursor;
    if (!cursor) break;
  }
  return out;
}

/** SHA-256 einer Datei (Web Crypto). */
export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Wiederaufnehmbarer Upload in Chunks (POST uploads -> PUT Content-Range -> complete). */
export async function uploadFile(file: File, onProgress: (sent: number, total: number) => void, opts: { assetId?: string } = {}) {
  const hash = sha256Hex(file);
  const init = await api.post<{ uploadId: string; uploadUrl: string; chunkSize: number }>('/media/uploads', {
    fileName: file.name, sizeBytes: file.size, mimeType: file.type || 'application/octet-stream', ...(opts.assetId ? { assetId: opts.assetId } : {}),
  });
  let offset = 0;
  let retries = 0;
  while (offset < file.size) {
    const end = Math.min(file.size, offset + init.chunkSize);
    try {
      const r = await request<{ receivedBytes: number }>('PUT', init.uploadUrl, {
        raw: file.slice(offset, end),
        headers: { 'Content-Type': 'application/octet-stream', 'Content-Range': `bytes ${offset}-${end - 1}/${file.size}` },
      });
      offset = r.receivedBytes;
      retries = 0;
    } catch (e) {
      // Wiederaufnahme: Stand beim Server abfragen und dort weitermachen
      if (++retries > 3) throw e;
      const st = await api.get<{ receivedBytes: number }>(init.uploadUrl);
      offset = st.receivedBytes;
    }
    onProgress(offset, file.size);
  }
  return api.post<{ assetId: string; versionId: string; status: string }>(`/media/uploads/${init.uploadId}/complete`, { sha256: await hash });
}
